import { type BridgeEvent, decodeBridgeEvent, ROUTES } from "./protocol";

export const EVENTS_RETRY_MIN_MS = 500;
export const EVENTS_RETRY_MAX_MS = 10_000;

export type EventsState = "unsupported" | "connecting" | "open" | "retrying" | "failed";

export interface EventsChannel {
  subscribeEvents(
    onEvent: (event: BridgeEvent) => void,
    onState: (state: EventsState) => void,
  ): () => void;
}

/** A bridge that answered and refused has told us something final; only a transport that
 *  died is worth retrying. */
class Refused extends Error {}

export class HttpEventsChannel implements EventsChannel {
  private minRetryMs: number;
  private maxRetryMs: number;

  constructor(
    private baseUrl: string,
    opts: { minRetryMs?: number; maxRetryMs?: number } = {},
  ) {
    this.minRetryMs = opts.minRetryMs ?? EVENTS_RETRY_MIN_MS;
    this.maxRetryMs = opts.maxRetryMs ?? EVENTS_RETRY_MAX_MS;
  }

  subscribeEvents(
    onEvent: (event: BridgeEvent) => void,
    onState: (state: EventsState) => void,
  ): () => void {
    let disposed = false;
    const controller = new AbortController();
    const report = (state: EventsState) => {
      if (!disposed) onState(state);
    };

    const run = async () => {
      let delay = this.minRetryMs;
      while (!disposed) {
        report("connecting");
        try {
          await this.read(
            controller.signal,
            (event) => {
              if (!disposed) onEvent(event);
            },
            () => report("open"),
            // A bridge with no HID attached holds the stream open and never writes, so
            // headers alone can't reset the backoff — only a delivered byte can.
            () => {
              delay = this.minRetryMs;
            },
          );
        } catch (err) {
          if (disposed) return;
          if (err instanceof Refused) {
            report("failed");
            return;
          }
        }
        if (disposed) return;
        report("retrying");
        await new Promise((resolve) => setTimeout(resolve, delay));
        delay = Math.min(this.maxRetryMs, delay * 2);
      }
    };
    void run();

    return () => {
      disposed = true;
      controller.abort();
    };
  }

  private async read(
    signal: AbortSignal,
    onEvent: (event: BridgeEvent) => void,
    onOpen: () => void,
    onData: () => void,
  ): Promise<void> {
    const res = await fetch(`${this.baseUrl}${ROUTES.events}`, { signal });
    if (!res.ok) throw new Refused(`${ROUTES.events} answered ${res.status}`);
    const body = res.body;
    if (!body) throw new Refused(`${ROUTES.events} answered with no body`);
    onOpen();
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffered = "";
    let gotData = false;
    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) return;
        if (!gotData) {
          gotData = true;
          onData();
        }
        buffered += decoder.decode(value, { stream: true });
        let cut = buffered.indexOf("\n");
        while (cut >= 0) {
          const event = decodeBridgeEvent(buffered.slice(0, cut));
          buffered = buffered.slice(cut + 1);
          if (event) onEvent(event);
          cut = buffered.indexOf("\n");
        }
      }
    } finally {
      // Cancelling an errored or aborted reader rejects with that same error; the throw
      // already in flight is the one worth reporting.
      await reader.cancel().catch(() => {});
    }
  }
}

export const NO_EVENTS: EventsChannel = {
  subscribeEvents(_onEvent, onState) {
    onState("unsupported");
    return () => {};
  },
};
